import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  CalendarRange,
  ClipboardList,
  Download,
  FileSpreadsheet,
  Filter,
  MapPin,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  SlidersHorizontal,
  Trash2,
  X,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import UserSectionSidebar from '../components/UserSectionSidebar';
import DateTimePicker from '../components/DateTimePicker';
import ConfirmModal from '../components/ConfirmModal';
import LoadingIndicator from '../components/LoadingIndicator';
import ThemedMultiSelect from '../components/ThemedMultiSelect';
import ThemedSelect from '../components/ThemedSelect';
import SellerOrderFormModal, {
  createEmptySellerOrderForm,
  createSellerOrderFormFromOrder,
} from '../components/SellerOrderFormModal';
import { useNotification } from '../components/NotificationProvider';
import { useTranslations } from '../translations';
import type {
  Location,
  SellerOrder,
  SellerOrderLocationOption,
  SellerOrderOverview,
  SellerOrderProductOption,
  SellerOrderRequest,
  SellerOrderStatus,
} from '../services/api.types';
import {
  createSellerOrder,
  deleteSellerOrder,
  fetchSellerOrderLocationOptions,
  fetchSellerOrderProductOptions,
  fetchSellerOrders,
  updateSellerOrder,
  updateSellerOrderStatus,
} from '../services/seller-order.api';
import {
  exportOrdersCsv,
  exportOrdersPdf,
} from '../utils/sellerOrderExport';
import type { AccountPageProps } from './accountPage.types';
import { formatUnitType } from '../utils/unitType';
import { orderDeliveryDate } from '../utils/sellerOrderDisplay';
import { localDateValue, toEndOfDayInstant, toStartOfDayInstant } from '../utils/dateRange';
import './Orders.css';

const initialOrderSearchEnd = new Date();
const initialOrderSearchStart = new Date(initialOrderSearchEnd);
initialOrderSearchStart.setDate(initialOrderSearchStart.getDate() - 30);
initialOrderSearchStart.setHours(0, 0, 0, 0);
const initialOrderSearchRange = {
  from: localDateValue(initialOrderSearchStart),
  to: localDateValue(initialOrderSearchEnd),
};

const money = (value: number) => new Intl.NumberFormat('ro-RO', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
}).format(value || 0);
const quantity = (value: number) => new Intl.NumberFormat('ro-RO', {
  maximumFractionDigits: 3,
}).format(value || 0);
const displayDate = (value: string | null) => value
  ? new Intl.DateTimeFormat('ro-RO', {
    day: '2-digit', month: 'short', year: 'numeric',
  }).format(new Date(value))
  : '—';

const statusClass = (status: SellerOrderStatus) => `order-status order-status-${status.toLowerCase()}`;

interface AppliedOrderFilters {
  customerName: string;
  statuses: SellerOrderStatus[];
  locationId: string;
  orderLocationId: string;
  dateFrom: string;
  dateTo: string;
}

const Orders: React.FC<AccountPageProps> = ({
  isLoggedIn,
  isAuthChecking,
  isAdmin = false,
  onNavigateToLogin,
  onNavigateToRegister,
  onNavigateToHome,
  onNavigateToProfile,
  onNavigateToLocations,
  onNavigateToMyAnnouncements,
  onNavigateToSettings,
  onNavigateToAdministration,
  onLogout,
  onNavigateToCreateProduct,
  onNavigateToMessages,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookies,
  onNavigateToAbout,
  onNavigateToContact,
}) => {
  const t = useTranslations();
  const copy = t.orders;
  const { showNotification } = useNotification();
  const [overview, setOverview] = useState<SellerOrderOverview | null>(null);
  const [products, setProducts] = useState<SellerOrderProductOption[]>([]);
  const [locationOptions, setLocationOptions] = useState<SellerOrderLocationOption[]>([]);
  const [statusFilters, setStatusFilters] = useState<SellerOrderStatus[]>([]);
  const [customerNameFilter, setCustomerNameFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [orderLocationFilter, setOrderLocationFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(initialOrderSearchRange.from);
  const [dateTo, setDateTo] = useState(initialOrderSearchRange.to);
  const [appliedFilters, setAppliedFilters] = useState<AppliedOrderFilters | null>(null);
  const [loading, setLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(!isAuthChecking);
  const [exporting, setExporting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState<SellerOrder | null>(null);
  const [cancellingOrder, setCancellingOrder] = useState(false);
  const [orderToDeleteId, setOrderToDeleteId] = useState<string | null>(null);
  const [deletingOrder, setDeletingOrder] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [initialForm, setInitialForm] = useState<SellerOrderRequest>(createEmptySellerOrderForm);
  const [initialDeliveryLocation, setInitialDeliveryLocation] = useState<Location | null>(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const mainLocationOptions = useMemo(() => Array.from(
    new Map(locationOptions.map((location) => [
      location.locationId,
      { value: location.locationId, label: `${location.locationName}, ${location.countyName}` },
    ])).values(),
  ).sort((first, second) => first.label.localeCompare(second.label, 'ro')), [locationOptions]);
  const deliveryPointOptions = useMemo(() => locationOptions
    .filter((location) => location.locationId === locationFilter)
    .map((location) => ({
      value: location.id,
      label: `${location.name}${location.addressDetails ? ` · ${location.addressDetails}` : ''}${location.active ? '' : ' (Arhivat)'}`,
    })), [locationFilter, locationOptions]);

  const loadOrderOptions = useCallback(async () => {
    if (isAuthChecking) return;
    if (!isLoggedIn || isAdmin) {
      onNavigateToLogin();
      return;
    }
    setOptionsLoading(true);
    try {
      const [options, locations] = await Promise.all([
        fetchSellerOrderProductOptions(),
        fetchSellerOrderLocationOptions(),
      ]);
      setProducts(options);
      setLocationOptions(locations);
      setLocationFilter((current) => current && !locations.some((location) => location.locationId === current)
        ? ''
        : current);
      setOrderLocationFilter((current) => current && !locations.some((location) => location.id === current)
        ? ''
        : current);
    } catch (error) {
      console.error('Could not load seller order options', error);
      showNotification(copy.optionsLoadError, 'error');
    } finally {
      setOptionsLoading(false);
    }
  }, [
    copy.optionsLoadError,
    isAdmin,
    isAuthChecking,
    isLoggedIn,
    onNavigateToLogin,
    showNotification,
  ]);

  useEffect(() => {
    loadOrderOptions();
  }, [loadOrderOptions]);

  const loadOrders = useCallback(async (
    filters = appliedFilters,
    options: { silent?: boolean } = {},
  ) => {
    if (!filters) return;
    const silent = options.silent ?? false;
    if (!silent) setLoading(true);
    try {
      setOverview(await fetchSellerOrders({
        customerName: filters.customerName.trim() || undefined,
        statuses: filters.statuses,
        locationId: filters.locationId || undefined,
        orderLocationId: filters.orderLocationId || undefined,
        from: toStartOfDayInstant(filters.dateFrom),
        to: toEndOfDayInstant(filters.dateTo),
      }));
    } catch (error) {
      console.error('Could not load seller orders', error);
      if (!silent) setOverview(null);
      showNotification(copy.loadError, 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [appliedFilters, copy.loadError, showNotification]);

  const statusesKey = [...statusFilters].sort().join(',');
  const appliedStatusesKey = [...(appliedFilters?.statuses ?? [])].sort().join(',');
  const filtersDirty = !appliedFilters
    || dateFrom !== appliedFilters.dateFrom
    || dateTo !== appliedFilters.dateTo
    || customerNameFilter !== appliedFilters.customerName
    || locationFilter !== appliedFilters.locationId
    || orderLocationFilter !== appliedFilters.orderLocationId
    || statusesKey !== appliedStatusesKey;
  const activeFilterCount = useMemo(() => [
    Boolean(customerNameFilter.trim()),
    statusFilters.length > 0,
    Boolean(locationFilter),
    Boolean(orderLocationFilter),
  ].filter(Boolean).length, [customerNameFilter, locationFilter, orderLocationFilter, statusFilters]);

  const applyFilters = async () => {
    if (!dateFrom || !dateTo) {
      showNotification(copy.intervalRequired, 'error');
      return;
    }
    if (dateFrom > dateTo) {
      showNotification(copy.invalidInterval, 'error');
      return;
    }
    const filters = {
      customerName: customerNameFilter,
      statuses: [...statusFilters],
      locationId: locationFilter,
      orderLocationId: orderLocationFilter,
      dateFrom,
      dateTo,
    };
    setAppliedFilters(filters);
    await loadOrders(filters);
  };

  const handleApplyFilters = () => {
    if (dateFrom && dateTo && dateFrom <= dateTo) {
      setShowMobileFilters(false);
    }
    void applyFilters();
  };

  const handleResetFilters = () => {
    if (!dateFrom || !dateTo) {
      showNotification(copy.intervalRequired, 'error');
      return;
    }
    if (dateFrom > dateTo) {
      showNotification(copy.invalidInterval, 'error');
      return;
    }

    const resetFilters: AppliedOrderFilters = {
      customerName: '',
      statuses: [],
      locationId: '',
      orderLocationId: '',
      dateFrom,
      dateTo,
    };
    setCustomerNameFilter('');
    setStatusFilters([]);
    setLocationFilter('');
    setOrderLocationFilter('');
    setAppliedFilters(resetFilters);
    void loadOrders(resetFilters);
  };

  useEffect(() => {
    if (!showMobileFilters) return;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowMobileFilters(false);
    };
    const closeOnDesktopResize = () => {
      if (window.innerWidth > 768) setShowMobileFilters(false);
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', closeOnDesktopResize);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', closeOnDesktopResize);
    };
  }, [showMobileFilters]);

  const openCreate = () => {
    setEditingOrderId(null);
    setInitialForm(createEmptySellerOrderForm());
    setInitialDeliveryLocation(null);
    setShowForm(true);
  };

  const openEdit = (order: SellerOrder, locationName: string, countyName: string) => {
    setEditingOrderId(order.id);
    setInitialForm(createSellerOrderFormFromOrder(order));
    setInitialDeliveryLocation({
      id: order.deliveryLocationId,
      name: locationName,
      diacriticsName: locationName,
      county: { id: '', name: countyName },
      coordinates: { latitude: 0, longitude: 0 },
      defaultLocation: false,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingOrderId(null);
  };

  const saveOrder = async (payload: SellerOrderRequest) => {
    try {
      if (editingOrderId) await updateSellerOrder(editingOrderId, payload);
      else await createSellerOrder(payload);
      showNotification(copy.saveSuccess, 'success');
      setShowForm(false);
      setEditingOrderId(null);
      void loadOrders(undefined, { silent: true });
      void loadOrderOptions();
    } catch (error) {
      console.error('Could not save seller order', error);
      showNotification(copy.saveError, 'error');
      throw error;
    }
  };

  const changeStatus = async (order: SellerOrder, status: SellerOrderStatus) => {
    if (status === 'CANCELLED') setCancellingOrder(true);
    try {
      await updateSellerOrderStatus(order.id, status);
      showNotification(copy.statusSuccess, 'success');
      if (status === 'CANCELLED') setOrderToCancel(null);
      await loadOrders(undefined, { silent: true });
    } catch (error) {
      console.error('Could not update seller order status', error);
      showNotification(copy.actionError, 'error');
    } finally {
      if (status === 'CANCELLED') setCancellingOrder(false);
    }
  };

  const removeOrder = async () => {
    if (!orderToDeleteId || deletingOrder) return;
    setDeletingOrder(true);
    try {
      await deleteSellerOrder(orderToDeleteId);
      showNotification(copy.deleteSuccess, 'success');
      setOrderToDeleteId(null);
      await loadOrders(undefined, { silent: true });
      await loadOrderOptions();
    } catch (error) {
      console.error('Could not delete seller order', error);
      showNotification(copy.actionError, 'error');
    } finally {
      setDeletingOrder(false);
    }
  };

  const runExport = async (format: 'CSV' | 'PDF') => {
    if (!overview) return;
    setExporting(true);
    try {
      const result = format === 'CSV'
        ? await exportOrdersCsv(overview)
        : await exportOrdersPdf(overview);
      if (result === 'downloaded') {
        showNotification(copy.exportStarted.replace('{format}', format), 'success');
      }
    } catch (error) {
      console.error('Could not export seller orders', error);
      showNotification(copy.exportError, 'error');
    } finally {
      setExporting(false);
    }
  };

  const summary = overview?.summary;

  return (
    <div className="orders-page">
      <Header
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToRegister={onNavigateToRegister}
        onNavigateToHome={onNavigateToHome}
        onLogout={onLogout}
        onProfileClick={onNavigateToProfile}
        onLocationsClick={onNavigateToLocations}
        onMessagesClick={onNavigateToMessages}
        onAnnouncementsClick={onNavigateToMyAnnouncements}
        onAdministrationClick={onNavigateToAdministration}
        onSettingsClick={onNavigateToSettings}
        onNavigateToCreateProduct={onNavigateToCreateProduct}
      />
      <div className="orders-container">
        <UserSectionSidebar
          activeItem="orders"
          onProfileClick={onNavigateToProfile}
          onLocationsClick={onNavigateToLocations}
          onMessagesClick={() => onNavigateToMessages?.()}
          onAnnouncementsClick={onNavigateToMyAnnouncements}
          onSettingsClick={onNavigateToSettings}
        />
        <main className="orders-main">
          <header className="orders-page-header">
            <div className="orders-page-heading">
              <span className="orders-page-eyebrow">
                <ClipboardList size={14} aria-hidden />
                {copy.eyebrow}
              </span>
              <h1>{copy.title}</h1>
              <p>{copy.subtitle}</p>
            </div>
            <button type="button" className="orders-primary-button" onClick={openCreate}>
              <Plus size={18} aria-hidden />
              {copy.add}
            </button>
          </header>

          <section className="seller-order-date-range orders-date-range" aria-label={copy.intervalLabel}>
            <div className="orders-section-heading">
              <span className="orders-section-icon" aria-hidden><CalendarRange size={20} /></span>
              <div>
                <h2>{copy.intervalLabel}</h2>
                <p>{copy.intervalHint}</p>
              </div>
            </div>
            <div className="seller-order-date-field">
              <span>{copy.dateFrom}</span>
              <DateTimePicker value={dateFrom} onChange={setDateFrom} ariaLabel={copy.dateFrom} clearLabel={copy.clearDateFrom} max={dateTo || undefined} dateOnly />
            </div>
            <div className="seller-order-date-field">
              <span>{copy.dateTo}</span>
              <DateTimePicker value={dateTo} onChange={setDateTo} ariaLabel={copy.dateTo} clearLabel={copy.clearDateTo} min={dateFrom || undefined} dateOnly />
            </div>
            <button
              type="button"
              className="seller-order-date-submit"
              disabled={!dateFrom || !dateTo || !filtersDirty || loading}
              onClick={handleApplyFilters}
            >
              <Filter size={16} aria-hidden /> {copy.applyInterval}
            </button>
          </section>

          <button
            type="button"
            className="orders-mobile-filters-button"
            aria-controls="orders-filters"
            aria-expanded={showMobileFilters}
            onClick={() => setShowMobileFilters(true)}
          >
            <SlidersHorizontal size={18} aria-hidden />
            <span>{copy.filters}</span>
            {activeFilterCount > 0 && (
              <span className="orders-mobile-filter-count" aria-label={copy.activeFilters.replace('{count}', activeFilterCount.toString())}>
                {activeFilterCount}
              </span>
            )}
          </button>

          {showMobileFilters && (
            <button
              type="button"
              className="orders-mobile-filters-overlay"
              aria-label={copy.closeFilters}
              onClick={() => setShowMobileFilters(false)}
            />
          )}

          <section
            id="orders-filters"
            className={`orders-toolbar ${showMobileFilters ? 'orders-mobile-visible' : ''}`}
            aria-label={copy.filtersTitle}
          >
            <div className="orders-mobile-filters-header">
              <h2>{copy.filters}</h2>
              <button type="button" aria-label={copy.closeFilters} onClick={() => setShowMobileFilters(false)}>
                <X size={20} aria-hidden />
              </button>
            </div>
            <div className="orders-filter-heading">
              <span className="orders-section-icon" aria-hidden><SlidersHorizontal size={19} /></span>
              <div>
                <h2>{copy.filtersTitle}</h2>
                <p>{copy.filtersHint}</p>
              </div>
            </div>
            <div className="orders-toolbar-filters">
              <div className="orders-toolbar-field orders-customer-filter">
                <span>{copy.customerFilter}</span>
                <div className="orders-customer-search">
                  <Search size={17} aria-hidden />
                  <input
                    type="search"
                    value={customerNameFilter}
                    maxLength={50}
                    placeholder={copy.customerFilterPlaceholder}
                    aria-label={copy.customerFilter}
                    autoComplete="off"
                    onChange={(event) => setCustomerNameFilter(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') handleApplyFilters();
                    }}
                  />
                </div>
              </div>
              <div className="orders-toolbar-field orders-status-filter">
                <span>{copy.statusFilter}</span>
                <ThemedMultiSelect
                  values={statusFilters}
                  onChange={setStatusFilters}
                  ariaLabel={copy.statusFilter}
                  allLabel={copy.allStatuses}
                  options={[
                    { value: 'PENDING', label: copy.pending },
                    { value: 'DELIVERED', label: copy.delivered },
                    { value: 'CANCELLED', label: copy.cancelled },
                  ]}
                />
              </div>
              <div className="orders-toolbar-field orders-location-filter">
                <span>{copy.locationFilter}</span>
                <ThemedSelect
                  value={locationFilter}
                  onChange={(value) => {
                    setLocationFilter(value);
                    setOrderLocationFilter('');
                  }}
                  ariaLabel={copy.locationFilter}
                  options={[{ value: '', label: copy.allLocations }, ...mainLocationOptions]}
                />
              </div>
              <div className="orders-toolbar-field orders-location-filter">
                <span>{copy.deliveryPointFilter}</span>
                <ThemedSelect
                  value={orderLocationFilter}
                  onChange={setOrderLocationFilter}
                  ariaLabel={copy.deliveryPointFilter}
                  disabled={!locationFilter}
                  options={locationFilter
                    ? [{ value: '', label: copy.allDeliveryPoints }, ...deliveryPointOptions]
                    : [{ value: '', label: copy.selectMainLocationFirst }]}
                />
              </div>
            </div>
            <div className="orders-mobile-filter-actions">
              <button
                type="button"
                className="orders-mobile-filter-apply"
                disabled={!dateFrom || !dateTo || !filtersDirty || loading}
                onClick={handleApplyFilters}
              >
                <Filter size={17} aria-hidden />
                {copy.applyFilters}
              </button>
              <button
                type="button"
                className="orders-mobile-filter-reset"
                disabled={activeFilterCount === 0 || loading}
                onClick={handleResetFilters}
              >
                <RotateCcw size={17} aria-hidden />
                {copy.resetFilters}
              </button>
            </div>
          </section>

          <div className="orders-export-row" role="group" aria-label={copy.exportOrders}>
            <div className="orders-export-actions">
              <button type="button" disabled={exporting || loading || filtersDirty || !overview} onClick={() => runExport('CSV')}>
                <FileSpreadsheet size={17} aria-hidden /> CSV
              </button>
              <button type="button" disabled={exporting || loading || filtersDirty || !overview} onClick={() => runExport('PDF')}>
                <Download size={17} aria-hidden /> PDF
              </button>
            </div>
          </div>

          {summary && (
            <section className="orders-summary" aria-label="Sumar comenzi">
              <div><span>{copy.totalOrders}</span><strong>{summary.orderCount}</strong></div>
              <div><span>{copy.expected}</span><strong>{money(summary.expectedAmount)} lei</strong></div>
              <div><span>{copy.deliveredAmount}</span><strong>{money(summary.deliveredAmount)} lei</strong></div>
              <div><span>{copy.cancelledAmount}</span><strong>{money(summary.cancelledAmount)} lei</strong></div>
            </section>
          )}

          {loading || optionsLoading ? (
            <LoadingIndicator message={copy.loading} className="orders-state" />
          ) : !appliedFilters ? (
            <div className="orders-state orders-empty">{copy.selectInterval}</div>
          ) : !overview || overview.locations.length === 0 ? (
            <div className="orders-state orders-empty">{copy.empty}</div>
          ) : (
            <div className="orders-locations">
              {overview.locations.map((location) => (
                <section
                  className="orders-location"
                  key={`${location.orderLocationId}-${location.deliveryPointName}-${location.deliveryAddressDetails || ''}`}
                >
                  <header className="orders-location-header">
                    <div className="orders-location-title">
                      <MapPin size={19} aria-hidden />
                      <div>
                        <h2>
                          {location.locationName}, {location.countyName}
                          {location.deliveryPointName.localeCompare(
                            location.locationName,
                            'ro',
                            { sensitivity: 'base' },
                          ) === 0 ? '' : ` - ${location.deliveryPointName}`}
                        </h2>
                        <span>
                          {[
                            location.deliveryAddressDetails,
                            location.orderCount === 1
                              ? copy.oneOrder
                              : copy.orderCount.replace('{count}', String(location.orderCount)),
                          ].filter(Boolean).join(' · ')}
                        </span>
                      </div>
                    </div>
                    <div className="orders-location-total">
                      <span>{copy.locationTotal}</span>
                      <strong>{money(location.expectedAmount)} lei</strong>
                    </div>
                  </header>
                  <div className="orders-products">
                    {location.products.map((product) => (
                      <section className="orders-product" key={`${product.productId || 'deleted'}-${product.productTitle}`}>
                        <header className="orders-product-header">
                          <div>
                            <h3>{product.productTitle}</h3>
                            <span>{quantity(product.activeQuantity)} {formatUnitType(product.unitType)}</span>
                          </div>
                          <div>
                            <span>{copy.productTotal}</span>
                            <strong>{money(product.expectedAmount)} lei</strong>
                          </div>
                        </header>
                        <div className="orders-table-wrap">
                          <table className="orders-table">
                            <thead>
                              <tr>
                                <th>{copy.customer}</th><th>{copy.contact}</th><th>{copy.quantity}</th><th>{copy.unit}</th>
                                <th>{copy.total}</th><th>{copy.orderDate}</th><th>{copy.deliveryDate}</th><th>Status</th>
                                <th>{copy.remarks}</th><th>{copy.actions}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {product.orders.map((order) => (
                                <tr key={order.id} className={order.status === 'CANCELLED' ? 'cancelled' : ''}>
                                  <td data-label={copy.customer}><strong>{order.customerName}</strong></td>
                                  <td data-label={copy.contact}>{order.customerPhone || '—'}</td>
                                  <td data-label={copy.quantity}>{quantity(order.quantity)}</td>
                                  <td data-label={copy.unit}>{formatUnitType(order.unitType)}</td>
                                  <td data-label={copy.total}><strong>{money(order.totalPrice)} lei</strong></td>
                                  <td data-label={copy.orderDate}>{displayDate(order.orderDate)}</td>
                                  <td data-label={copy.deliveryDate}>{displayDate(orderDeliveryDate(order))}</td>
                                  <td data-label="Status"><span className={statusClass(order.status)}>{copy[order.status === 'PENDING' ? 'pending' : order.status === 'DELIVERED' ? 'delivered' : 'cancelled']}</span></td>
                                  <td data-label={copy.remarks} className="orders-remarks">{order.remarks || '—'}</td>
                                  <td data-label={copy.actions}>
                                    <div className="orders-row-actions">
                                      {order.status !== 'CANCELLED' && (
                                        <button type="button" onClick={() => openEdit(order, location.locationName, location.countyName)} aria-label={copy.edit} title={copy.edit}>
                                          <Pencil size={16} aria-hidden />
                                        </button>
                                      )}
                                      {order.status === 'PENDING' && (
                                        <button type="button" onClick={() => changeStatus(order, 'DELIVERED')} aria-label={copy.markDelivered} title={copy.markDelivered}>
                                          <Check size={17} aria-hidden />
                                        </button>
                                      )}
                                      {order.status === 'DELIVERED' && (
                                        <button type="button" onClick={() => changeStatus(order, 'PENDING')} aria-label={copy.restorePending} title={copy.restorePending}>
                                          <RotateCcw size={16} aria-hidden />
                                        </button>
                                      )}
                                      {order.status === 'CANCELLED' && (
                                        <button type="button" onClick={() => changeStatus(order, 'PENDING')} aria-label={copy.reactivateOrder} title={copy.reactivateOrder}>
                                          <RotateCcw size={16} aria-hidden />
                                        </button>
                                      )}
                                      {order.status === 'PENDING' && (
                                        <button type="button" className="warning" onClick={() => setOrderToCancel(order)} aria-label={copy.cancelOrder} title={copy.cancelOrder}>
                                          <X size={17} aria-hidden />
                                        </button>
                                      )}
                                      <button type="button" className="warning" onClick={() => setOrderToDeleteId(order.id)} aria-label={copy.deleteOrder} title={copy.deleteOrder}>
                                        <Trash2 size={16} aria-hidden />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </section>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </main>
      </div>

      {showForm && (
        <SellerOrderFormModal
          title={editingOrderId ? copy.edit : copy.add}
          products={products}
          initialForm={initialForm}
          initialDeliveryLocation={initialDeliveryLocation}
          onClose={closeForm}
          onSubmit={saveOrder}
        />
      )}

      {orderToDeleteId && (
        <ConfirmModal
          title={copy.deleteDialogTitle}
          message={copy.deleteConfirm}
          confirmText={copy.deleteDialogConfirm}
          cancelText={t.common.cancel}
          destructive
          isConfirming={deletingOrder}
          onConfirm={() => void removeOrder()}
          onCancel={() => setOrderToDeleteId(null)}
        />
      )}

      {orderToCancel && (
        <ConfirmModal
          title={copy.cancelDialogTitle}
          message={copy.cancelConfirm}
          confirmText={copy.cancelDialogConfirm}
          cancelText={t.common.cancel}
          destructive
          isConfirming={cancellingOrder}
          onConfirm={() => void changeStatus(orderToCancel, 'CANCELLED')}
          onCancel={() => setOrderToCancel(null)}
        />
      )}

      <Footer
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookies={onNavigateToCookies}
        onNavigateToAbout={onNavigateToAbout}
        onNavigateToContact={onNavigateToContact}
      />
    </div>
  );
};

export default Orders;
